import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BasicSetupFormToolbar } from "../BasicSetupFormToolbar";
import { ChequeSettingsInfoSetupFieldsPanel } from "./Panels/ChequeSettingsInfoSetupFieldsPanel";
import { ChequeSettingsService } from "../../services/ChequeSettingsService";
import { ChequeSettingsSetupToolbar } from "./ChequeSettingsSetupToolbar";
import { ChequeSettingsSetupValidationSchema } from "./validator";
import { DDLDataTable } from "../DDLDataTable";
import { IAppState } from "../../store/reducers";
import { IChequeSettings } from "../../models/ChequeSettings";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IChequeSettingsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "chequeSettings-single-data-record";
// const CHEQUE_SETTINGS_LIST_DATA_QUERY_KEY = "cheque-settings-list-data-record";

const useGetData = (
	clientId: string,
	// id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return ChequeSettingsService.getByClientId(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

// const useChequeSettings = (
// 	clientId: string,
// 	params: IGetListRequest,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [CHEQUE_SETTINGS_LIST_DATA_QUERY_KEY, params],
// 		queryFn: (data) => {
// 			return ChequeSettingsService.getChequeSettings(
// 				clientId,
// 				params,
// 				navigateToLogout
// 			);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

export const ChequeSettingsSetup: React.FC<IChequeSettingsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [chequeSettingsId, setChequeSettingsId] = useState<number>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();
	// const [editRecordId, setEditRecordId] = useState(id);

	const { isPending: isSubmitting, mutate: submitChequeSettingsFormFields } =
		useMutation({
			mutationFn: ChequeSettingsService.create,
			onSuccess: (res: IChequeSettings | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIChequeSettingsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/settings/cheque/setup/${clientId}`;
					navigate(path);
					return Promise.all([
						// queryClient.invalidateQueries({
						// 	queryKey: [CHEQUE_SETTINGS_LIST_DATA_QUERY_KEY],
						// }),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIChequeSettingsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/settings/cheque/setup/${clientId}`;
					navigate(path);
					return Promise.all([
						// queryClient.invalidateQueries({
						// 	queryKey: [CHEQUE_SETTINGS_LIST_DATA_QUERY_KEY],
						// }),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		// editRecordId,
		navigateToLogout
	);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	// const { isPending, data } = useChequeSettings(
	// 	clientId,
	// 	queryParams,
	// 	navigateToLogout
	// );

	// const chequeSettingsList: any | null = data;

	// const upadtedChequeSettingsListItems = useMemo(() => {
	// 	return (
	// 		chequeSettingsList &&
	// 		chequeSettingsList.items &&
	// 		chequeSettingsList?.items.map((item: any) => ({
	// 			...item,
	// 			label: (
	// 				<Link
	// 					to={`/settings/cheque/setup/${item.id}`}
	// 					className="no-underline"
	// 					style={{ color: theme.palette.primary.light }}
	// 					onClick={() => setEditRecordId(item.id)}
	// 				>
	// 					{item.name}
	// 				</Link>
	// 			),
	// 		}))
	// 	);
	// }, [chequeSettingsList]);

	const DEFAULT_VALUES = {
		chequeFormat: 500,
		dateFormat: 300,
		printDateIndicators: 100,
		printAddress: 100,
		printChequeNo: 100,
		printPaymentDate: 100,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(ChequeSettingsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {

		let payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			chequeFormat: values.chequeFormat,
			dateFormat: values.dateFormat,
			printDateIndicators: values.printDateIndicators,
			printAddress: values.printAddress,
			printChequeNo: values.printChequeNo,
			printPaymentDate: values.printPaymentDate,
		};
		submitChequeSettingsFormFields(payload);
	};

	useEffect(() => {
		if (clientId && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "clientId") {
					let path = `/settings/cheque/setup/${clientId}`
					navigate(path)
				}
			});
		}
	}, [clientId, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteChequeSettingsById } =
		useMutation({
			mutationFn: ChequeSettingsService.delChequeSettings,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the ChequeSettings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/settings/cheque/setup`;
				navigate(path);
				return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [CHEQUE_SETTINGS_LIST_DATA_QUERY_KEY],
					// }),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteChequeSettingsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				chequeSettingsId: chequeSettingsId,
				navigateToLogout: navigateToLogout,
			};
			deleteChequeSettingsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setChequeSettingsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setChequeSettingsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveChequeSettingsById } =
		useMutation({
			mutationFn: ChequeSettingsService.archiveChequeSettings,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the ChequeSettings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/settings/cheque/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveChequeSettingsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				chequeSettingsId: chequeSettingsId,
				navigateToLogout: navigateToLogout,
			};
			archiveChequeSettingsById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setChequeSettingsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: IChequeSettings | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/settings/cheque/setup/${params.value}`;
		navigate(path);
	}, []);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/settings/cheque/setup`;
		navigate(path);
	};

	const expandedLabels = ["Settings", "Cheque"];
	const expandedChequeSettingsSetupOrSetupItem = "Cheque"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Cheque" &&
				item.label === expandedChequeSettingsSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedChequeSettingsSetupOrSetupItem && "/settings/cheque/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		chequeSettingsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
		// sx={{ pb: "5rem", "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteChequeSettingsField}
				cancelActionHandler={onCloseHandler}
				testId="chequeSettings-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="ChequeSettings"
										testId={`chequeSettings`}
										showTitle={false}
										variant="outlined"
									>
										<ChequeSettingsInfoSetupFieldsPanel />
									</StandardPanel>
								}
								toolbar={
									<BasicSetupFormToolbar
										isSubmitting={isSubmitting}
										pageTitle={"Stub Settings"}
									/>
								}
								testId="form-layout"
								showFullDiaplay={true}
								toolbarPosition="top"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						{/* <Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									chequeSettingsList && upadtedChequeSettingsListItems
										? upadtedChequeSettingsListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									chequeSettingsList && chequeSettingsList.total
										? chequeSettingsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
							/>
						</Grid> */}
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			{/* <EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveChequeSettingsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/> */}

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"chequeSettings-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
